import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    Target,
    Compass,
    Users,
    Award,
    Clock,
    CheckCircle,
    ArrowRight,
    Lightbulb,
    Handshake,
    TrendingUp,
    DollarSign,
} from 'lucide-react';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const AboutPage = () => {
    const values = [
        {
            icon: Handshake,
            title: 'Collaboration',
            description: 'Deeper integration with client teams for seamless project delivery.',
        },
        {
            icon: TrendingUp,
            title: 'Operational Efficiency',
            description: 'Optimized processes and technology for maximum productivity.',
        },
        {
            icon: DollarSign,
            title: 'Cost Reduction',
            description: 'Lowered OPEX through smart, sustainable solutions.',
        },
        {
            icon: Lightbulb,
            title: 'Business Value',
            description: 'Improved productivity and stakeholder satisfaction.',
        },
    ];

    const stats = [
        { number: '30+', label: 'Years Experience', icon: Clock },
        { number: '500+', label: 'Projects Completed', icon: CheckCircle },
        { number: '200+', label: 'Enterprise Clients', icon: Users },
        { number: '15+', label: 'Industry Awards', icon: Award },
    ];

    return (
        <div data-testid="about-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0c3xlbnwwfHx8fDE3NzIyMTEzNDZ8MA&ixlib=rb-4.1.0&q=85"
                        alt="Server room background"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            About Vanfrank
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="about-hero-title"
                        >
                            Three Decades of ICT Excellence
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            Building the digital infrastructure that powers modern enterprise.
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Main About Section */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                                Who We Are
                            </span>
                            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                                Infrastructure and Technology Solutions Provisioning
                            </h2>
                            <div className="space-y-4 text-slate-600 leading-relaxed">
                                <p>
                                    Our organization functions as a comprehensive provider of Network Infrastructure
                                    and Information Communications Technology solutions. We specialize in delivering
                                    bespoke offerings designed to enhance operational efficiency, minimize expenditures,
                                    and optimize the overall business value for our clients.
                                </p>
                                <p>
                                    For three decades, our firm has provided comprehensive solutions to enterprises
                                    spanning numerous industries. These offerings integrate personnel, processes,
                                    and technology into a cohesive framework designed to maximize productivity
                                    and ensure operational excellence. This holistic approach has consistently
                                    delivered tangible improvements to our clientele's overall effectiveness.
                                </p>
                                <p>
                                    Our expertise spans Network Infrastructure, IT Service Management, Unified
                                    Communications, Data Center Design, Enterprise Connectivity, Security Systems,
                                    and Technology-driven Business Solutions. We collaborate closely with our
                                    clients, ensuring every solution aligns with their strategic objectives and
                                    supports long-term growth.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600">
                                <img
                                    src="https://images.unsplash.com/photo-1653565684985-0b1a64cf7afc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxibGFjayUyMHByb2Zlc3Npb25hbHMlMjB0ZWFtd29yayUyMGNvbGxhYm9yYXRpb24lMjBvZmZpY2V8ZW58MHx8fHwxNzcyNDgxMzgzfDA&ixlib=rb-4.1.0&q=85"
                                    alt="Vanfrank team collaboration"
                                    className="w-full h-[500px] object-cover mix-blend-luminosity opacity-40"
                                    loading="lazy"
                                />
                            </div>
                            <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-blue-500/10 rounded-2xl -z-10"></div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="py-24 md:py-32 bg-slate-50 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Vision */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            data-testid="about-vision"
                            className="bg-slate-900 rounded-2xl p-10 md:p-12 relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                            <div className="relative">
                                <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center mb-6">
                                    <Target className="text-blue-400" size={32} />
                                </div>
                                <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-4 block">
                                    Our Vision
                                </span>
                                <h3 className="text-3xl font-bold text-white mb-4">
                                    To consistently enhance productivity.
                                </h3>
                                <p className="text-slate-400 leading-relaxed">
                                    We envision a future where every organization leverages technology
                                    to achieve operational excellence, sustainable growth, and competitive
                                    advantage in an increasingly digital world.
                                </p>
                            </div>
                        </motion.div>

                        {/* Mission */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            data-testid="about-mission"
                            className="bg-blue-500 rounded-2xl p-10 md:p-12 relative overflow-hidden"
                        >
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/30 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                            <div className="relative">
                                <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mb-6">
                                    <Compass className="text-white" size={32} />
                                </div>
                                <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-200 mb-4 block">
                                    Our Mission
                                </span>
                                <h3 className="text-3xl font-bold text-white mb-4">
                                    To enrich businesses through customized solutions.
                                </h3>
                                <p className="text-blue-100 leading-relaxed">
                                    We are committed to driving sustainable profitability for our clientele
                                    and ourselves through innovative, tailored technology solutions that
                                    address unique business challenges.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                            Our Track Record
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                            Numbers That Speak
                        </h2>
                    </motion.div>

                    <div data-testid="about-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center p-8 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:border-blue-500/20 transition-all duration-300"
                            >
                                <stat.icon className="mx-auto text-blue-500 mb-4" size={32} />
                                <div className="font-mono text-4xl font-bold text-slate-900 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-slate-500 font-medium">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Paradigm */}
            <section className="py-24 md:py-32 bg-slate-900 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-4 block">
                            Vanfrank Service Paradigm
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                            Our Commitment to Excellence
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            We deliver technology solutions that directly support business strategy,
                            ensuring measurable outcomes and sustainable growth.
                        </p>
                    </motion.div>

                    <div data-testid="service-paradigm" className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-blue-500/30 transition-all duration-300"
                            >
                                <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center mb-6">
                                    <value.icon className="text-blue-400" size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                                <p className="text-slate-400 leading-relaxed">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                            Ready to Partner with Us?
                        </h2>
                        <p className="text-slate-600 text-lg mb-10">
                            Let's discuss how Vanfrank can help transform your organization's
                            technology infrastructure.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="about-contact-cta"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors shadow-[0_0_30px_rgba(15,23,42,0.3)]"
                        >
                            Get In Touch
                            <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
